import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import { Users, MessageSquare, Radio, Shield, RefreshCw, Trash2, Clock, Ban, ShieldOff, Undo2, Globe2, X as XIcon, TrendingUp } from "lucide-react";

const SOURCE_TONE = {
  tiktok: "#FF3BFF",     // magenta (TikTok brand vibe)
  instagram: "#FF3BFF",
  reddit: "#FF3B30",     // reddit orange-red
  google: "#00E5FF",     // cyan
  twitter: "#00E5FF",
  x: "#00E5FF",
  youtube: "#FF3B30",
  facebook: "#00E5FF",
  discord: "#7CFF00",
  direct: "#F4F4F0",
};

function sourceColor(src) {
  return SOURCE_TONE[src] || "#D4FF00";
}

function Stat({ label, value, sub, testid, tone = "acid" }) {
  const border = tone === "blaze" ? "border-blaze" : tone === "hotpink" ? "border-hotpink" : "border-acid";
  const color = tone === "blaze" ? "text-blaze" : tone === "hotpink" ? "text-hotpink" : "text-acid";
  return (
    <div data-testid={testid} className={`border-2 ${border} bg-panel/60 p-4 md:p-5`}>
      <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest">{label}</div>
      <div className={`font-heading text-4xl md:text-5xl mt-1 ${color}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

function Bar({ pct, tone = "acid" }) {
  const bg = tone === "blaze" ? "bg-blaze" : tone === "hotpink" ? "bg-hotpink" : "bg-acid";
  return (
    <div className="h-1.5 bg-white/10 w-full">
      <div className={`h-full ${bg}`} style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
    </div>
  );
}

function relTime(mins) {
  if (mins == null) return "unknown";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function fmtDur(sec) {
  if (!sec || sec < 60) return `${sec || 0}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ${sec % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [sessionSummary, setSessionSummary] = useState(null);
  const [activeDays, setActiveDays] = useState(7);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingSlug, setDeletingSlug] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [clearingSeed, setClearingSeed] = useState(false);

  const seedTraffic = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/traffic-sources/seed?count=120&days=30");
      toast.success(`SEEDED ${data.inserted} synthetic hits over ${data.days} days`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to seed traffic.");
    } finally {
      setSeeding(false);
    }
  };

  const clearSeededTraffic = async () => {
    if (clearingSeed) return;
    if (!window.confirm("Delete ALL synthetic seed rows? Real traffic will not be touched.")) return;
    setClearingSeed(true);
    try {
      const { data } = await api.delete("/admin/traffic-sources/seed");
      toast.success(`REMOVED ${data.deleted} seed rows`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear seed.");
    } finally {
      setClearingSeed(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [s, r, au, ss] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/rooms").catch(() => ({ data: [] })),
        api.get(`/admin/users/active?days=${activeDays}`).catch(() => ({ data: { users: [] } })),
        api.get("/admin/sessions/summary").catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setRooms(r.data || []);
      setActiveUsers(au.data?.users || []);
      setSessionSummary(ss.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load stats.");
    } finally {
      setLoading(false);
    }
  };

  const deleteRoom = async (room) => {
    if (deletingSlug) return;
    const ok = window.confirm(
      `Delete "${room.name}"?\n\n` +
      `${room.message_count} messages will be permanently removed. ` +
      `Any live members will be disconnected.\n\nThis can't be undone.`
    );
    if (!ok) return;
    setDeletingSlug(room.slug);
    try {
      await api.delete(`/rooms/${encodeURIComponent(room.slug)}`);
      setRooms((rs) => rs.filter((r) => r.slug !== room.slug));
      toast.success(`ROOM DELETED · #${room.slug}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete room.");
    } finally {
      setDeletingSlug(null);
    }
  };

  const banUser = async (target) => {
    if (target.banned) return;
    const ok = window.confirm(
      `Ban @${target.username}?\n\n` +
      `This permanently deactivates their account and blacklists their last known IP so ` +
      `they can't re-register from the same device. Existing messages stay for audit — ` +
      `use the trash icon in the chat to remove individual posts.\n\nThis can't be undone.`
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/users/${encodeURIComponent(target.username)}/ban`);
      setActiveUsers((us) =>
        us.map((u) => (u.username === target.username ? { ...u, banned: true } : u))
      );
      toast.success(
        `BANNED @${target.username} · ${data.banned_ips.length} IP${data.banned_ips.length === 1 ? "" : "s"} blacklisted`
      );
      loadBannedIps();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to ban user.");
    }
  };

  const unbanUser = async (target) => {
    if (!target.banned) return;
    const ok = window.confirm(
      `Unban @${target.username}?\n\n` +
      `This reactivates the account and frees any IPs that were blacklisted specifically for them.`
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/users/${encodeURIComponent(target.username)}/unban`);
      setActiveUsers((us) =>
        us.map((u) => (u.username === target.username ? { ...u, banned: false } : u))
      );
      toast.success(
        `UNBANNED @${target.username} · ${data.ips_freed} IP${data.ips_freed === 1 ? "" : "s"} freed`
      );
      loadBannedIps();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unban user.");
    }
  };

  const [bannedIps, setBannedIps] = useState([]);
  const [dupOnly, setDupOnly] = useState(false);
  const [ipModal, setIpModal] = useState(null); // { ip, users, banlisted }
  const loadBannedIps = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/banned-ips");
      setBannedIps(data.ips || []);
    } catch { /* silent */ }
  }, []);

  const removeBannedIp = async (ip) => {
    const ok = window.confirm(`Remove ${ip} from the blacklist? New signups from this IP will be allowed.`);
    if (!ok) return;
    try {
      await api.delete(`/admin/banned-ips/${encodeURIComponent(ip)}`);
      setBannedIps((xs) => xs.filter((x) => x.ip !== ip));
      toast.success(`${ip} freed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to free IP.");
    }
  };

  const openIpModal = async (ip) => {
    if (!ip) return;
    try {
      const { data } = await api.get(`/admin/ips/${encodeURIComponent(ip)}/users`);
      setIpModal(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch IP report.");
    }
  };

  useEffect(() => { load(); loadBannedIps(); }, [activeDays, loadBannedIps]);

  // Auto-refresh the pulse every 30s so metrics reflect live activity.
  // Pauses when the tab is hidden to avoid pointless polling.
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") load(); };
    const id = setInterval(tick, 30_000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDays]);

  if (user === null) return null;
  if (!user || !user.is_admin) return <Navigate to="/lobby" replace />;

  const maxTopRoom = stats?.top_rooms_7d?.[0]?.messages_7d || 1;
  const maxTopUser = stats?.top_users_7d?.[0]?.messages_7d || 1;

  return (
    <div className="min-h-screen bg-obsidian text-bone" data-testid="admin-dashboard">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 font-mono text-[10px] text-hotpink uppercase tracking-widest">
              <Shield className="w-3 h-3" /> Admin // Restricted
            </div>
            <h1 className="font-heading text-4xl md:text-5xl mt-1">ENGAGEMENT <span className="text-acid">PULSE</span></h1>
            <div className="font-mono text-[11px] text-white/40 mt-1">
              Live metrics for this environment only. Deploy to sync with production data.
            </div>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="font-mono text-xs px-4 py-2 border-2 border-acid text-acid hover:bg-acid hover:text-obsidian uppercase tracking-widest flex items-center gap-2"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {err && <div className="border-2 border-blaze text-blaze font-mono text-xs p-3 mb-4">{err}</div>}
        {loading && !stats && <div className="font-mono text-acid">LOADING…</div>}

        {stats && (
          <>
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-3 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                <Users className="w-3 h-3" /> Users
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat testid="stat-users-total" label="Total users" value={stats.users.total} sub={`${stats.users.registered} registered · ${stats.users.guests} guests`} />
                <Stat testid="stat-users-engaged" label="Ever posted" value={stats.users.engaged_ever} sub="Real engagement" tone="hotpink" />
                <Stat testid="stat-users-active-24h" label="Active (24h)" value={stats.users.active_24h} sub={`${stats.users.logins_24h ?? 0} logged in · +${stats.users.new_24h} new`} tone="blaze" />
                <Stat testid="stat-users-active-7d" label="Active (7d)" value={stats.users.active_7d} sub={`${stats.users.logins_7d ?? 0} logged in · +${stats.users.new_7d} new`} />
              </div>
            </section>

            <section className="mb-8">
              <div className="flex items-center gap-2 mb-3 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                <MessageSquare className="w-3 h-3" /> Messages & DMs
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat testid="stat-msgs-total" label="Messages total" value={stats.messages.total} />
                <Stat testid="stat-msgs-24h" label="Last 24h" value={stats.messages.last_24h} tone="blaze" />
                <Stat testid="stat-msgs-7d" label="Last 7d" value={stats.messages.last_7d} tone="hotpink" />
                <Stat testid="stat-dms" label="DMs sent" value={stats.dms.total} />
              </div>
            </section>

            {sessionSummary && (
              <section className="mb-8" data-testid="sessions-panel">
                <div className="flex items-center gap-2 mb-3 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                  <Clock className="w-3 h-3" /> Session duration · last 7 days
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat testid="stat-sessions-open" label="Open now" value={sessionSummary.open_now} sub={`${sessionSummary.sessions_24h} in 24h`} tone="acid" />
                  <Stat testid="stat-sessions-7d" label="Total (7d)" value={sessionSummary.sessions_7d} sub={`${sessionSummary.total_sessions} lifetime`} />
                  <Stat testid="stat-session-avg" label="Avg duration" value={fmtDur(sessionSummary.avg_duration_seconds_7d)} sub={`Median ${fmtDur(sessionSummary.median_duration_seconds_7d)}`} tone="hotpink" />
                  <Stat testid="stat-session-longest" label="Longest" value={fmtDur(sessionSummary.longest_duration_seconds_7d)} sub="single session" tone="blaze" />
                </div>
                {sessionSummary.top_sessions_7d?.length > 0 && (
                  <div className="mt-3 border-2 border-white/10 bg-panel/40 divide-y divide-white/10" data-testid="top-sessions">
                    <div className="p-3 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                      Longest sessions · last 7d
                    </div>
                    {sessionSummary.top_sessions_7d.map((s, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 p-3 font-mono text-xs">
                        <span className="text-acid truncate">@{s.username}</span>
                        <span className="text-white/60 shrink-0">{fmtDur(s.duration_seconds)}</span>
                        <span className="text-white/30 text-[10px] shrink-0">{new Date(s.started_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            <section className="mb-8">
              <div className="flex items-center gap-2 mb-3 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                <Radio className="w-3 h-3" /> Rooms
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat testid="stat-rooms-total" label="Rooms total" value={stats.rooms.total} />
                <Stat testid="stat-rooms-approved" label="Approved" value={stats.rooms.approved} />
                <Stat testid="stat-rooms-pending" label="Pending" value={stats.rooms.pending} tone="blaze" />
                <Stat testid="stat-rooms-rejected" label="Rejected" value={stats.rooms.rejected} tone="hotpink" />
              </div>
            </section>

            <section className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div data-testid="top-rooms" className="border-2 border-white/10 bg-panel/60 p-5">
                <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mb-3">
                  Top rooms · last 7d
                </div>
                {stats.top_rooms_7d.length === 0 && <div className="font-mono text-xs text-white/40">No data yet.</div>}
                <ul className="space-y-3">
                  {stats.top_rooms_7d.map((r) => (
                    <li key={r.slug}>
                      <div className="flex items-center justify-between font-mono text-xs mb-1">
                        <span className="text-acid">#{r.slug}</span>
                        <span className="text-white/60">{r.messages_7d}</span>
                      </div>
                      <Bar pct={(r.messages_7d / maxTopRoom) * 100} tone="acid" />
                    </li>
                  ))}
                </ul>
              </div>

              <div data-testid="top-users" className="border-2 border-white/10 bg-panel/60 p-5">
                <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mb-3">
                  Most active users · last 7d
                </div>
                {stats.top_users_7d.length === 0 && <div className="font-mono text-xs text-white/40">No data yet.</div>}
                <ul className="space-y-3">
                  {stats.top_users_7d.map((u) => (
                    <li key={u.username}>
                      <div className="flex items-center justify-between font-mono text-xs mb-1">
                        <span className="text-hotpink">@{u.username}</span>
                        <span className="text-white/60">{u.messages_7d}</span>
                      </div>
                      <Bar pct={(u.messages_7d / maxTopUser) * 100} tone="hotpink" />
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="mb-8" data-testid="active-users-panel">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                  <Users className="w-3 h-3" /> Active users · last {activeDays} days
                </div>
                <div className="flex items-center gap-1">
                  {[1, 7, 30].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setActiveDays(d)}
                      data-testid={`active-window-${d}d`}
                      className={`font-mono text-[10px] px-2 py-1 uppercase tracking-widest border ${
                        activeDays === d
                          ? "border-acid text-obsidian bg-acid"
                          : "border-white/20 text-white/60 hover:border-white/40"
                      }`}
                    >
                      {d}d
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setDupOnly((v) => !v)}
                    data-testid="filter-duplicate-ip"
                    title="Show only users sharing an IP with another account"
                    className={`ml-2 font-mono text-[10px] px-3 py-1.5 border-2 uppercase tracking-widest transition-colors ${
                      dupOnly
                        ? "border-blaze text-obsidian bg-blaze"
                        : "border-white/20 text-white/60 hover:border-blaze/60 hover:text-blaze"
                    }`}
                  >
                    Dup IP
                  </button>
                </div>
              </div>
              {(dupOnly ? activeUsers.filter((u) => u.duplicate_ip) : activeUsers).length === 0 ? (
                <div className="border-2 border-white/10 bg-panel/40 p-4 font-mono text-xs text-white/40">
                  {dupOnly ? "No users with a duplicated IP." : `No users active in the last ${activeDays} day${activeDays === 1 ? "" : "s"}.`}
                </div>
              ) : (
                <div className="border-2 border-white/10 bg-panel/40 divide-y divide-white/10">
                  {(dupOnly ? activeUsers.filter((u) => u.duplicate_ip) : activeUsers).map((u) => (
                    <div
                      key={u.id}
                      data-testid={`active-user-${u.username}`}
                      className={`flex items-center gap-3 p-3 md:p-4 hover:bg-white/[0.02] group border-l-4 ${
                        u.banned ? "border-l-blaze opacity-60" : u.duplicate_ip ? "border-l-blaze/60" : u.online ? "border-l-acid" : "border-l-transparent"
                      }`}
                    >
                      <a href={`/u/${u.username}`} className="relative shrink-0">
                        <img src={u.avatar} alt="" className="w-9 h-9 object-cover border border-white/10 group-hover:border-acid" />
                        {u.online && !u.banned && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-acid border-2 border-obsidian animate-pulse" />}
                      </a>
                      <a href={`/u/${u.username}`} className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm truncate" style={{ color: u.text_color || "#F4F4F0" }}>
                            @{u.username}
                          </span>
                          {u.is_guest && (
                            <span className="font-mono text-[9px] text-white/60 border border-white/20 px-1.5 py-0.5 uppercase tracking-widest">
                              Guest
                            </span>
                          )}
                          {u.banned && (
                            <span className="font-mono text-[9px] text-blaze border border-blaze px-1.5 py-0.5 uppercase tracking-widest">
                              Banned
                            </span>
                          )}
                          {u.duplicate_ip && !u.banned && (
                            <span className="font-mono text-[9px] text-blaze border border-blaze/60 px-1.5 py-0.5 uppercase tracking-widest">
                              Dup ×{u.ip_share_count}
                            </span>
                          )}
                          {u.online && !u.banned && (
                            <span className="font-mono text-[9px] text-acid uppercase tracking-widest">// online now</span>
                          )}
                        </div>
                        <div className="font-mono text-[10px] text-white/40 mt-0.5 flex items-center gap-2 flex-wrap">
                          <span>{relTime(u.minutes_ago)} · {u.messages_sent} msg{u.messages_sent === 1 ? "" : "s"}</span>
                          {u.last_ip && (
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); openIpModal(u.last_ip); }}
                              data-testid={`ip-link-${u.username}`}
                              title="Show all accounts on this IP"
                              className={`inline-flex items-center gap-1 hover:text-acid ${u.duplicate_ip ? "text-blaze" : "text-white/50"}`}
                            >
                              <Globe2 className="w-3 h-3" /> {u.last_ip}
                            </button>
                          )}
                        </div>
                      </a>
                      {u.username !== user?.username && (
                        u.banned ? (
                          <button
                            type="button"
                            onClick={() => unbanUser(u)}
                            data-testid={`unban-user-${u.username}`}
                            title={`Unban @${u.username}`}
                            className="shrink-0 font-mono text-[10px] px-3 py-2 border-2 border-acid/60 text-acid hover:bg-acid hover:text-obsidian uppercase tracking-widest flex items-center gap-1"
                          >
                            <Undo2 className="w-3 h-3" /> Unban
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => banUser(u)}
                            data-testid={`ban-user-${u.username}`}
                            title={`Ban @${u.username}`}
                            className="shrink-0 font-mono text-[10px] px-3 py-2 border-2 border-blaze/60 text-blaze hover:bg-blaze hover:text-obsidian uppercase tracking-widest flex items-center gap-1"
                          >
                            <Ban className="w-3 h-3" /> Ban
                          </button>
                        )
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mb-8" data-testid="admin-banned-ips-panel">
              <div className="flex items-center gap-2 mb-3 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                <ShieldOff className="w-3 h-3" /> Banned IPs · {bannedIps.length}
              </div>
              {bannedIps.length === 0 ? (
                <div className="border-2 border-white/10 bg-panel/40 p-4 font-mono text-xs text-white/40">
                  // no IPs on the blacklist right now
                </div>
              ) : (
                <div className="border-2 border-white/10 bg-panel/40 divide-y divide-white/10">
                  {bannedIps.map((row) => (
                    <div
                      key={row.ip}
                      data-testid={`banned-ip-${row.ip}`}
                      className="flex items-center gap-3 p-3 md:p-4 hover:bg-white/[0.02]"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-blaze truncate">{row.ip}</div>
                        <div className="font-mono text-[10px] text-white/40 mt-0.5">
                          {row.banned_username ? `@${row.banned_username} · ` : ""}
                          by @{row.banned_by || "system"}
                          {row.created_at ? ` · ${new Date(row.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeBannedIp(row.ip)}
                        data-testid={`unban-ip-${row.ip}`}
                        title={`Free ${row.ip}`}
                        className="shrink-0 font-mono text-[10px] px-3 py-2 border-2 border-acid/60 text-acid hover:bg-acid hover:text-obsidian uppercase tracking-widest flex items-center gap-1"
                      >
                        <Undo2 className="w-3 h-3" /> Free
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mb-8" data-testid="admin-rooms-panel">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                  <Radio className="w-3 h-3" /> Rooms · least active first
                </div>
                <div className="font-mono text-[10px] text-white/40">
                  {rooms.filter((r) => r.message_count === 0).length} dead ·
                  {" "}{rooms.filter((r) => (r.days_idle ?? 0) >= 7 && r.message_count > 0).length} idle 7d+
                </div>
              </div>
              {rooms.length === 0 ? (
                <div className="border-2 border-white/10 bg-panel/40 p-4 font-mono text-xs text-white/40">
                  No rooms to manage.
                </div>
              ) : (
                <div className="border-2 border-white/10 bg-panel/40 divide-y divide-white/10">
                  {rooms.map((r) => {
                    const dead = r.message_count === 0;
                    const idle = !dead && (r.days_idle ?? 0) >= 7;
                    const tone = dead ? "border-l-blaze" : idle ? "border-l-hotpink" : "border-l-acid";
                    return (
                      <div
                        key={r.slug}
                        className={`flex items-center gap-4 p-3 md:p-4 border-l-4 ${tone} hover:bg-white/[0.02] flex-wrap`}
                        data-testid={`admin-room-${r.slug}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-heading text-xl md:text-2xl truncate">{r.name}</span>
                            {r.preset && (
                              <span className="font-mono text-[9px] text-obsidian bg-acid px-1.5 py-0.5 uppercase tracking-widest">
                                Preset
                              </span>
                            )}
                            {dead && (
                              <span className="font-mono text-[9px] text-blaze border border-blaze px-1.5 py-0.5 uppercase tracking-widest">
                                Dead
                              </span>
                            )}
                            {idle && (
                              <span className="font-mono text-[9px] text-hotpink border border-hotpink px-1.5 py-0.5 uppercase tracking-widest">
                                Idle
                              </span>
                            )}
                            {r.live_members > 0 && (
                              <span className="font-mono text-[9px] text-acid border border-acid px-1.5 py-0.5 uppercase tracking-widest flex items-center gap-1">
                                <span className="w-1 h-1 bg-acid animate-pulse" /> {r.live_members} live
                              </span>
                            )}
                          </div>
                          <div className="font-mono text-[10px] text-white/40 mt-1 truncate">
                            #{r.slug} · by @{r.created_by}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 font-mono text-[11px] text-white/60">
                          <div className="text-right">
                            <div className="text-white/80">{r.message_count}</div>
                            <div className="text-[9px] text-white/40">msgs</div>
                          </div>
                          <div className="text-right flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <div>
                              <div className="text-white/80">{r.days_idle ?? "—"}d</div>
                              <div className="text-[9px] text-white/40">idle</div>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteRoom(r)}
                          disabled={deletingSlug === r.slug}
                          data-testid={`admin-delete-${r.slug}`}
                          className="font-mono text-[10px] px-3 py-2 border-2 border-blaze text-blaze hover:bg-blaze hover:text-obsidian uppercase tracking-widest flex items-center gap-1 disabled:opacity-40"
                          title="Delete room (cascades all messages)"
                        >
                          <Trash2 className="w-3 h-3" />
                          {deletingSlug === r.slug ? "…" : "Delete"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <div className="font-mono text-[10px] text-white/30 text-center">
              Generated {new Date(stats.generated_at).toLocaleString()}
            </div>

            {stats.traffic_sources && (
              <section className="mt-8" data-testid="traffic-sources-section">
                <div className="flex items-center gap-2 mb-3 font-mono text-[10px] text-hotpink uppercase tracking-widest">
                  <TrendingUp className="w-3 h-3" /> Traffic sources · last 30 days
                  <span className="flex-1" />
                  <button
                    type="button"
                    onClick={seedTraffic}
                    disabled={seeding}
                    data-testid="seed-traffic-btn"
                    className="font-mono text-[10px] px-2 py-1 border border-acid/60 text-acid hover:bg-acid hover:text-obsidian uppercase tracking-widest disabled:opacity-40"
                    title="Insert 120 synthetic hits spread across the last 30 days so you can preview the panel."
                  >
                    {seeding ? "seeding…" : "+ seed test data"}
                  </button>
                  <button
                    type="button"
                    onClick={clearSeededTraffic}
                    disabled={clearingSeed}
                    data-testid="clear-seed-btn"
                    className="font-mono text-[10px] px-2 py-1 border border-white/20 text-white/50 hover:border-blaze hover:text-blaze uppercase tracking-widest disabled:opacity-40"
                    title="Remove synthetic seed rows (real traffic is preserved)."
                  >
                    {clearingSeed ? "clearing…" : "clear seed"}
                  </button>
                </div>
                <div className="border-2 border-hotpink/30 bg-panel/60 p-5">
                  {/* Top-line visitor stats — the honest headcount */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                    <div>
                      <div className="text-white/40">Unique visitors · 30d</div>
                      <div className="font-heading text-3xl text-acid mt-1" data-testid="traffic-unique-30d">{stats.traffic_sources.unique_visitors_30d || 0}</div>
                    </div>
                    <div>
                      <div className="text-white/40">Registered</div>
                      <div className="font-heading text-3xl text-acid mt-1" data-testid="traffic-registered-30d">{stats.traffic_sources.registered_30d || 0}</div>
                    </div>
                    <div>
                      <div className="text-white/40">Guests</div>
                      <div className="font-heading text-3xl text-hotpink mt-1" data-testid="traffic-guests-30d">{stats.traffic_sources.guests_30d || 0}</div>
                    </div>
                    <div>
                      <div className="text-white/40">Anonymous</div>
                      <div className="font-heading text-3xl text-white/80 mt-1" data-testid="traffic-anonymous-30d">{stats.traffic_sources.anonymous_30d || 0}</div>
                    </div>
                    <div>
                      <div className="text-white/40">Returning</div>
                      <div className="font-heading text-3xl text-cyan mt-1" data-testid="traffic-returning-30d">{stats.traffic_sources.returning_30d || 0}</div>
                    </div>
                  </div>

                  {/* Raw hit counts as a secondary row */}
                  <div className="grid grid-cols-3 gap-4 mb-5 font-mono text-[10px] text-white/40 uppercase tracking-widest border-t border-white/10 pt-4">
                    <div>
                      Hits · 7d <span className="text-white/70 ml-1" data-testid="traffic-hits-7d">{stats.traffic_sources.hits_7d || 0}</span>
                    </div>
                    <div>
                      Hits · 30d <span className="text-white/70 ml-1" data-testid="traffic-hits-30d">{stats.traffic_sources.hits_30d || 0}</span>
                    </div>
                    <div>
                      All time <span className="text-white/70 ml-1" data-testid="traffic-hits-all">{stats.traffic_sources.hits_all || 0}</span>
                    </div>
                  </div>

                  {stats.traffic_sources.by_source_30d.length === 0 ? (
                    <div className="font-mono text-white/40 text-sm" data-testid="traffic-empty">
                      // no traffic tracked yet · share links like ?source=tiktok or wait for referrer traffic to arrive
                    </div>
                  ) : (
                    <div className="space-y-4" data-testid="traffic-source-list">
                      {(() => {
                        const rows = stats.traffic_sources.by_source_30d;
                        const max = rows[0]?.unique_visitors || rows[0]?.hits || 1;
                        return rows.map((r) => (
                          <div key={r.source} data-testid={`traffic-row-${r.source}`}>
                            <div className="flex items-baseline justify-between mb-1 flex-wrap gap-2">
                              <div className="flex items-center gap-2">
                                <span
                                  className="w-2 h-2 rounded-full inline-block"
                                  style={{ background: sourceColor(r.source) }}
                                />
                                <span className="font-mono text-sm text-white" data-testid={`traffic-source-name-${r.source}`}>{r.source}</span>
                                {r.source === "direct" && (
                                  <span className="font-mono text-[9px] text-white/40">// direct / unknown</span>
                                )}
                              </div>
                              <div className="font-mono text-[11px] text-white/70 flex items-center gap-2 flex-wrap">
                                <span className="text-acid" title="Distinct devices in the last 30 days" data-testid={`traffic-visitors-${r.source}`}>
                                  {r.unique_visitors} visitor{r.unique_visitors === 1 ? "" : "s"}
                                </span>
                                <span className="text-white/40">·</span>
                                <span data-testid={`traffic-count-${r.source}`}>{r.hits} hit{r.hits === 1 ? "" : "s"}</span>
                                {r.registered > 0 && <><span className="text-white/40">·</span><span className="text-acid" title="Registered accounts">reg {r.registered}</span></>}
                                {r.guests > 0 && <><span className="text-white/40">·</span><span className="text-hotpink" title="Guest accounts">guest {r.guests}</span></>}
                                {r.anonymous > 0 && <><span className="text-white/40">·</span><span title="Anonymous (not signed in)">anon {r.anonymous}</span></>}
                                {r.returning > 0 && <><span className="text-white/40">·</span><span className="text-cyan" title="Came back on ≥ 2 different days">ret {r.returning}</span></>}
                              </div>
                            </div>
                            <div className="h-1.5 bg-white/10 w-full">
                              <div
                                className="h-full transition-all"
                                style={{ width: `${Math.max(2, (r.unique_visitors / max) * 100)}%`, background: sourceColor(r.source) }}
                              />
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                  <div className="mt-5 pt-4 border-t border-white/10 font-mono text-[10px] text-white/40 leading-relaxed">
                    // <span className="text-acid">visitor</span> = one distinct device (localStorage UUID · not IP · not account)
                    <br />
                    // <span className="text-hotpink">hit</span> = one arrival at the site · same visitor over multiple days can add hits without adding visitors
                    <br />
                    // <span className="text-cyan">returning</span> = came back on ≥ 2 different days · <span className="text-acid">reg</span>/<span className="text-hotpink">guest</span>/anon = latest sign-in state
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {ipModal && (
        <div
          data-testid="ip-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-obsidian/90 backdrop-blur-sm p-4"
          onClick={() => setIpModal(null)}
        >
          <div
            className="bg-panel border-2 border-blaze/40 max-w-lg w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b-2 border-white/10">
              <div className="flex items-center gap-2 min-w-0">
                <Globe2 className="w-4 h-4 text-blaze shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono text-xs text-white/40 uppercase tracking-widest">IP Report</div>
                  <div className="font-mono text-sm text-blaze truncate">{ipModal.ip}</div>
                </div>
                {ipModal.banlisted && (
                  <span className="font-mono text-[9px] text-blaze border border-blaze px-1.5 py-0.5 uppercase tracking-widest shrink-0">
                    Blacklisted
                  </span>
                )}
              </div>
              <button
                type="button"
                data-testid="close-ip-modal"
                onClick={() => setIpModal(null)}
                className="text-white/60 hover:text-white p-1"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mb-2">
                {ipModal.count} account{ipModal.count === 1 ? "" : "s"} on this IP
              </div>
              <div className="divide-y divide-white/10 border-2 border-white/10">
                {(ipModal.users || []).map((u) => (
                  <a
                    key={u.id}
                    href={`/u/${u.username}`}
                    data-testid={`ip-modal-user-${u.username}`}
                    className="flex items-center gap-3 p-3 hover:bg-white/[0.02]"
                  >
                    <img src={u.avatar} alt="" className="w-8 h-8 object-cover border border-white/10 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm truncate">@{u.username}</span>
                        {u.is_guest && (
                          <span className="font-mono text-[9px] text-white/60 border border-white/20 px-1.5 py-0.5 uppercase tracking-widest">
                            Guest
                          </span>
                        )}
                        {u.banned && (
                          <span className="font-mono text-[9px] text-blaze border border-blaze px-1.5 py-0.5 uppercase tracking-widest">
                            Banned
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-[10px] text-white/40 mt-0.5">
                        {u.last_ip === ipModal.ip ? "last · " : "signup · "}
                        {u.last_seen ? new Date(u.last_seen).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
